import apiClient from './apiClient';

export const registerUser = async (payload) => {
  const response = await apiClient.post('/api/users/register', payload);
  return response.data;
};

export const loginUser = async (payload) => {
  const response = await apiClient.post('/api/users/login', payload);
  return response.data;
};

export const verifyLoginOtp = async (payload) => {
  const response = await apiClient.post('/api/users/login/verify-otp', payload);
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await apiClient.get('/api/users/me');
  return response.data;
};

export const acceptTerms = async () => {
  const response = await apiClient.post('/api/users/me/accept-terms');
  return response.data;
};

export const getUserProfile = async (userIdOrUsername) => {
  // @mention links resolve by username; everywhere else uses the numeric id.
  const isNumericId = /^\d+$/.test(String(userIdOrUsername));
  const url = isNumericId
    ? `/api/users/${userIdOrUsername}/profile`
    : `/api/users/username/${encodeURIComponent(userIdOrUsername)}/profile`;
  const response = await apiClient.get(url);
  return response.data;
};

export const searchMentionSuggestions = async (query) => {
  const response = await apiClient.get('/api/users/mention-suggestions', { params: { q: query } });
  return response.data;
};

export const updateUserProfile = async (userId, payload) => {
  const response = await apiClient.put(`/api/users/${userId}/profile`, payload);
  return response.data;
};

export const uploadUserAvatar = async (userId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post(`/api/users/${userId}/profile/image`, formData);
  return response.data;
};

export const deleteUserAvatar = async (userId) => {
  const response = await apiClient.delete(`/api/users/${userId}/profile/image`);
  return response.data;
};

export const setDefaultAvatar = async (userId, avatarId) => {
  const response = await apiClient.post(`/api/users/${userId}/profile/default-avatar`, { avatarId });
  return response.data;
};

export const setRandomDefaultAvatar = async (userId) => {
  const response = await apiClient.post(`/api/users/${userId}/profile/default-avatar/random`);
  return response.data;
};

export const fetchDefaultAvatars = async () => {
  const response = await apiClient.get('/api/users/default-avatars');
  return response.data;
};

export const getUserBookmarks = async (userId, params = {}) => {
  const response = await apiClient.get(`/api/users/${userId}/bookmarks`, { params });
  return response.data;
};

export const getUserRatings = async (userId, params = {}) => {
  const response = await apiClient.get(`/api/users/${userId}/ratings`, { params });
  return response.data;
};

export const getMyReadingHistory = async () => {
  const response = await apiClient.get('/api/users/me/reading-history');
  return response.data;
};

export const getMyProgression = async () => {
  const response = await apiClient.get('/api/users/me/progression');
  return response.data;
};

export const recomputeMyProgression = async () => {
  const response = await apiClient.post('/api/users/me/progression/recompute');
  return response.data;
};

export const getUserLeaderboard = async (params = {}) => {
  const response = await apiClient.get('/api/users/leaderboard', { params });
  return response.data;
};

export const setProgressionDisplay = async (track) => {
  const response = await apiClient.put(`/api/users/me/progression-display?track=${track}`);
  return response.data;
};

export const setDisplayNamePreference = async (preference) => {
  const response = await apiClient.put(`/api/users/me/display-name-preference?preference=${preference}`);
  return response.data;
};

export const setSignatureBadge = async (badgeId) => {
  const params = badgeId != null ? `?badgeId=${badgeId}` : '';
  const response = await apiClient.put(`/api/users/me/signature-badge${params}`);
  return response.data;
};

// ==================== Admin: User Management ====================

export const getAllUsers = async () => {
  const response = await apiClient.get('/api/users');
  return response.data;
};

export const searchUsers = async (query) => {
  const response = await apiClient.get('/api/users/search', { params: { q: query } });
  return response.data;
};

export const setUserStatus = async (userId, status, reason = null, durationHours = null) => {
  const payload = { status };
  if (reason) payload.reason = reason;
  if (durationHours != null && durationHours > 0) payload.durationHours = durationHours;
  const response = await apiClient.put(`/api/users/${userId}/status`, payload);
  return response.data;
};

export const setUserRoles = async (userId, roles, reason = null) => {
  const payload = { roles };
  if (reason) payload.reason = reason;
  const response = await apiClient.put(`/api/users/${userId}/roles`, payload);
  return response.data;
};

// ==================== Admin: IP Ban Management ====================

export const getBannedIps = async () => {
  const response = await apiClient.get('/api/users/ip-bans');
  return response.data;
};

export const banIp = async (ipAddress, reason = null) => {
  const payload = { ipAddress };
  if (reason) payload.reason = reason;
  const response = await apiClient.post('/api/users/ip-bans', payload);
  return response.data;
};

export const unbanIp = async (ipAddress, reason = null) => {
  const params = {};
  if (reason) params.reason = reason;
  const response = await apiClient.delete(`/api/users/ip-bans/${encodeURIComponent(ipAddress)}`, { params });
  return response.data;
};

export const searchUsersByIp = async (ip) => {
  const response = await apiClient.get('/api/users/by-ip', { params: { ip } });
  return response.data;
};

// ==================== Moderation Actions (audit log) ====================

export const getModerationActions = async (page = 0, size = 20) => {
  const response = await apiClient.get('/api/moderation/actions', { params: { page, size } });
  return response.data;
};

export const getModerationSummary = async () => {
  const response = await apiClient.get('/api/moderation/summary');
  return response.data;
};

export const getNewUserActivity = async (hours = 48, page = 0, size = 20) => {
  const response = await apiClient.get('/api/moderation/new-users', { params: { hours, page, size } });
  return response.data;
};

export const getModerationActionsForUser = async (userId) => {
  const response = await apiClient.get(`/api/moderation/actions/user/${userId}`);
  return response.data;
};

export const getModerationActionsByModerator = async (moderatorId, page = 0, size = 20) => {
  const response = await apiClient.get(`/api/moderation/actions/by-moderator/${moderatorId}`, { params: { page, size } });
  return response.data;
};

// ==================== Spam alerts (user-level anti-spam queue) ====================

export const getSpamAlerts = async (page = 0, size = 20) => {
  const response = await apiClient.get('/api/moderation/spam-alerts', { params: { page, size } });
  return response.data;
};

export const dismissSpamAlert = async (alertId) => {
  await apiClient.post(`/api/moderation/spam-alerts/${alertId}/dismiss`);
};

export const resolveSpamAlert = async (alertId, { shadowBanUser = false, resolveFlags = false, reason = null } = {}) => {
  await apiClient.post(`/api/moderation/spam-alerts/${alertId}/resolve`, {
    shadowBanUser,
    resolveFlags,
    reason,
  });
};

// ==================== Auto-flagged comment queue ====================

// ==================== Global recent comments feed ====================

export const getRecentComments = async (page = 0, size = 20) => {
  const response = await apiClient.get('/api/moderation/recent-comments', { params: { page, size } });
  return response.data;
};

// ==================== User activity lookup (any user) ====================

export const getUserActivity = async (username) => {
  const response = await apiClient.get('/api/moderation/user-activity', { params: { username } });
  return response.data;
};

export const getFlaggedComments = async (page = 0, size = 20) => {
  const response = await apiClient.get('/api/moderation/flagged-comments', { params: { page, size } });
  return response.data;
};

export const dismissFlaggedComment = async (source, commentId) => {
  await apiClient.post(`/api/moderation/flagged-comments/${source}/${commentId}/dismiss`);
};

export const resolveFlaggedComment = async (source, commentId, { deleteComment = false, shadowBanUser = false, reason = null } = {}) => {
  await apiClient.post(`/api/moderation/flagged-comments/${source}/${commentId}/resolve`, {
    deleteComment,
    shadowBanUser,
    reason,
  });
};

// ==================== Password recovery ====================

export const requestPasswordReset = async (email) => {
  const response = await apiClient.post('/api/users/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await apiClient.post('/api/users/reset-password', { token, newPassword });
  return response.data;
};

// ==================== Change password (authenticated) ====================

export const changePassword = async (currentPassword, newPassword) => {
  const response = await apiClient.put('/api/users/me/password', { currentPassword, newPassword });
  return response.data;
};

// ==================== Change username (authenticated) ====================

export const changeUsername = async (newUsername) => {
  const response = await apiClient.put('/api/users/me/username', { username: newUsername });
  return response.data;
};

// ==================== Email verification ====================

export const resendVerificationEmail = async (email) => {
  const response = await apiClient.post('/api/users/resend-verification', { email });
  return response.data;
};

// ==================== Email preferences ====================

export const getEmailPreferences = async () => {
  const response = await apiClient.get('/api/users/me/email-preferences');
  return response.data;
};

export const updateEmailPreferences = async (promotionalEmailsEnabled) => {
  const response = await apiClient.put('/api/users/me/email-preferences', { promotionalEmailsEnabled });
  return response.data;
};
